// ============================================================
// FEAT-03: src/pages/Racoes.js — Formulação de Ração
// Criar em /opt/ovinogest/frontend/src/pages/Racoes.js
// Adicionar rota em App.js: <Route path="/racoes" element={<Racoes />} />
// Adicionar item no menu Sidebar.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { Wheat, Plus, Play, Package, AlertTriangle, ChevronDown, ChevronUp, Trash2, Edit, BarChart3, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';

const GRUPOS_CONSUMO = {
  matrizes_prenhes:  'Matrizes Prenhes',
  matrizes_cordeiros:'Matrizes com Cordeiros ao Pé',
  matrizes_secas:    'Matrizes Secas',
  reprodutores:      'Reprodutores',
  borregos:          'Borregos',
  cordeiros:         'Cordeiros',
  lote:              'Lote Específico',
  outro:             'Outro',
};

const CATEGORIAS = {
  amamentacao: { label: 'Cordeiros em Amamentação', desc: '15 dias até desmame', cor: '#22c55e' },
  crescimento: { label: 'Crescimento', desc: '2 a 6 meses', cor: '#3b82f6' },
  terminacao:  { label: 'Terminação', desc: 'Acima de 6 meses', cor: '#f59e0b' },
  gestante:    { label: 'Ovelhas Gestantes', desc: 'Último terço gestação', cor: '#a855f7' },
  lactacao:    { label: 'Lactação', desc: 'Produção de leite', cor: '#ec4899' },
};

const Racoes = () => {
  const [receitas, setReceitas] = useState([]);
  const [materiasPrimas, setMateriasPrimas] = useState([]);
  const [producoes, setProducoes] = useState([]);
  const [consumos, setConsumos] = useState([]);
  const [programacoes, setProgramacoes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('receitas');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalProduzir, setModalProduzir] = useState(null);
  const [modalConsumo, setModalConsumo] = useState(false);
  const [modalProgramacao, setModalProgramacao] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [capacidade, setCapacidade] = useState(null);
  const hoje = todayLocalISO();
  const [formConsumo, setFormConsumo] = useState({
    receita_id: '', data: hoje, quantidade_kg: '', grupo_tipo: 'matrizes_prenhes', lote_id: '', observacao: '',
  });
  const [formProgramacao, setFormProgramacao] = useState({
    receita_id: '', data_inicio: hoje, quantidade_kg: '', grupo_tipo: 'lote', lote_id: '', observacao: '',
  });

  // Form state
  const [form, setForm] = useState({
    nome: '', categoria: 'amamentacao', descricao: '', rendimento_kg: 100,
    ingredientes: [],
  });
  const [editandoId, setEditandoId] = useState(null);
  const [multiplicador, setMultiplicador] = useState(1);

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, mpRes, prodRes, conRes, progRes, lotRes] = await Promise.all([
        api.get('/racoes/receitas'),
        api.get('/racoes/materias-primas'),
        api.get('/racoes/producao'),
        api.get('/racoes/consumo'),
        api.get('/racoes/consumo-programado'),
        api.get('/lotes'),
      ]);
      setReceitas(recRes.data);
      setMateriasPrimas(mpRes.data);
      setProducoes(prodRes.data);
      setConsumos(conRes.data);
      setProgramacoes(progRes.data);
      setLotes(lotRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error('Erro ao carregar dados de ração');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // Categorias com pelo menos uma receita cadastrada — cards E seletor de Nova Receita ficam
  // restritos a elas (escolha do responsável, ciente de que zerar todas as receitas de uma
  // categoria a tira do seletor até que outra receita seja criada nela por outro meio).
  // Ordem = a do dicionário CATEGORIAS.
  const categoriasComReceita = Object.keys(CATEGORIAS).filter(key => receitas.some(r => r.categoria === key));

  // ── Capacidade de produção ──
  const calcularCapacidade = async (receitaId) => {
    try {
      const { data } = await api.get(`/racoes/capacidade/${receitaId}`);
      setCapacidade(data);
    } catch (err) {
      console.error('Erro ao calcular capacidade:', err);
    }
  };

  // ── CRUD Receita ──
  const abrirModal = async (receita = null) => {
    if (receita) {
      try {
        const { data } = await api.get(`/racoes/receitas/${receita.id}`);
        setForm({
          nome: data.nome,
          categoria: data.categoria,
          descricao: data.descricao || '',
          rendimento_kg: data.rendimento_kg || 100,
          ingredientes: data.ingredientes || [],
        });
      } catch {
        setForm({
          nome: receita.nome,
          categoria: receita.categoria,
          descricao: receita.descricao || '',
          rendimento_kg: receita.rendimento_kg || 100,
          ingredientes: [],
        });
      }
      setEditandoId(receita.id);
    } else {
      setForm({ nome: '', categoria: categoriasComReceita[0] || '', descricao: '', rendimento_kg: 100, ingredientes: [] });
      setEditandoId(null);
    }
    setModalAberto(true);
  };

  const adicionarIngrediente = () => {
    setForm(prev => ({
      ...prev,
      ingredientes: [...prev.ingredientes, { produto_id: '', quantidade_kg: 0, percentual: 0 }],
    }));
  };

  const removerIngrediente = (index) => {
    setForm(prev => ({
      ...prev,
      ingredientes: prev.ingredientes.filter((_, i) => i !== index),
    }));
  };

  const atualizarIngrediente = (index, campo, valor) => {
    setForm(prev => ({
      ...prev,
      ingredientes: prev.ingredientes.map((ing, i) =>
        i === index ? { ...ing, [campo]: valor } : ing
      ),
    }));
  };

  const salvarReceita = async () => {
    if (!form.nome || !form.categoria) return toast.error('Nome e categoria são obrigatórios');
    try {
      if (editandoId) {
        await api.put(`/racoes/receitas/${editandoId}`, form);
        toast.success('Receita atualizada!');
      } else {
        await api.post('/racoes/receitas', form);
        toast.success('Receita criada!');
      }
      setModalAberto(false);
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar receita');
    }
  };

  const excluirReceita = async (id) => {
    if (!window.confirm('Excluir esta receita?')) return;
    try {
      await api.delete(`/racoes/receitas/${id}`);
      toast.success('Receita excluída');
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  // ── Produzir ──
  const abrirProduzir = async (receita) => {
    setModalProduzir(receita);
    setMultiplicador(1);
    await calcularCapacidade(receita.id);
  };

  const executarProducao = async () => {
    if (!modalProduzir || multiplicador <= 0) return;
    try {
      const { data } = await api.post('/racoes/produzir', {
        receita_id: modalProduzir.id,
        multiplicador,
      });
      toast.success(`${data.quantidade_produzida_kg} kg de "${data.receita}" produzidos!`);
      setModalProduzir(null);
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro na produção');
    }
  };

  // ── Consumo ──
  const registrarConsumo = async () => {
    if (!formConsumo.receita_id || !formConsumo.quantidade_kg) {
      return toast.error('Selecione a receita e informe a quantidade');
    }
    if (formConsumo.grupo_tipo === 'lote' && !formConsumo.lote_id) {
      return toast.error('Selecione o lote');
    }
    try {
      const { data } = await api.post('/racoes/consumo', {
        ...formConsumo,
        quantidade_kg: parseFloat(formConsumo.quantidade_kg),
        lote_id: formConsumo.grupo_tipo === 'lote' ? formConsumo.lote_id : null,
      });
      toast.success(`Consumo registrado! ${data.num_animais > 0 ? `Custo rateado para ${data.num_animais} animais (R$ ${data.custo_por_animal}/animal)` : ''}`);
      setModalConsumo(false);
      setFormConsumo({ receita_id: '', data: hoje, quantidade_kg: '', grupo_tipo: 'matrizes_prenhes', lote_id: '', observacao: '' });
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registrar consumo');
    }
  };

  const salvarProgramacao = async () => {
    if (!formProgramacao.receita_id || !formProgramacao.quantidade_kg || !formProgramacao.data_inicio) {
      return toast.error('Preencha receita, quantidade e data inicial');
    }
    if (formProgramacao.grupo_tipo === 'lote' && !formProgramacao.lote_id) {
      return toast.error('Selecione o lote');
    }
    try {
      await api.post('/racoes/consumo-programado', {
        ...formProgramacao,
        quantidade_kg: parseFloat(formProgramacao.quantidade_kg),
        lote_id: formProgramacao.grupo_tipo === 'lote' ? formProgramacao.lote_id : null,
      });
      toast.success('Consumo diário programado');
      setModalProgramacao(false);
      setFormProgramacao({ receita_id: '', data_inicio: hoje, quantidade_kg: '', grupo_tipo: 'lote', lote_id: '', observacao: '' });
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao programar consumo');
    }
  };

  const desativarProgramacao = async (id) => {
    if (!window.confirm('Desativar esta programação diária?')) return;
    try {
      await api.delete(`/racoes/consumo-programado/${id}`);
      toast.success('Programação desativada');
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao desativar');
    }
  };

  const executarProgramacoesHoje = async () => {
    try {
      const { data } = await api.post('/racoes/consumo-programado/executar-hoje');
      const ok = data.resultados?.filter(r => r.status === 'ok').length || 0;
      const erro = data.resultados?.filter(r => r.status === 'erro').length || 0;
      toast.success(`${ok} programação(ões) executada(s)${erro ? `, ${erro} com erro` : ''}`);
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao executar programações');
    }
  };

  // ── Detalhar receita ──
  const toggleDetalhe = async (receitaId) => {
    if (expandedId === receitaId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(receitaId);
    try {
      const { data } = await api.get(`/racoes/receitas/${receitaId}`);
      setReceitas(prev => prev.map(r => r.id === receitaId ? { ...r, ingredientes: data.ingredientes } : r));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container"><div className="spinner"></div><p>Carregando...</p></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <Wheat size={24} />
          <h1>Formulação de Ração</h1>
        </div>
        <div className="page-actions">
          {abaAtiva === 'consumo' && (
            <button className="btn btn-secondary" onClick={() => setModalConsumo(true)}>
              <Package size={16} /> Registrar Consumo
            </button>
          )}
          {abaAtiva === 'automatico' && (
            <>
              <button className="btn btn-secondary" onClick={executarProgramacoesHoje}>
                <Play size={16} /> Executar Hoje
              </button>
              <button className="btn btn-secondary" onClick={() => setModalProgramacao(true)}>
                <CalendarClock size={16} /> Programar Consumo
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => abrirModal()}>
            <Plus size={16} /> Nova Receita
          </button>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'receitas', label: 'Receitas', icon: <Wheat size={15} /> },
          { key: 'historico', label: 'Histórico de Produção', icon: <BarChart3 size={15} /> },
          { key: 'consumo', label: 'Consumo', icon: <Package size={15} /> },
          { key: 'automatico', label: 'Consumo Diário', icon: <CalendarClock size={15} /> },
        ].map(aba => (
          <button
            key={aba.key}
            onClick={() => setAbaAtiva(aba.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', border: 'none', cursor: 'pointer',
              borderBottom: abaAtiva === aba.key ? '2px solid var(--primary, #2D6A4F)' : '2px solid transparent',
              color: abaAtiva === aba.key ? 'var(--primary, #2D6A4F)' : '#6b7280',
              fontWeight: abaAtiva === aba.key ? 'bold' : 'normal',
              background: 'none', fontSize: '14px',
            }}
          >
            {aba.icon} {aba.label}
          </button>
        ))}
      </div>

      {/* ── ABA RECEITAS ── */}
      {abaAtiva === 'receitas' && (
        <div>
          {/* Cards por categoria */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {Object.entries(CATEGORIAS).filter(([key]) => categoriasComReceita.includes(key)).map(([key, cat]) => {
              const qtd = receitas.filter(r => r.categoria === key).length;
              return (
                <div key={key} className="card" style={{ padding: '14px', textAlign: 'center', borderTop: `3px solid ${cat.cor}` }}>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: cat.cor }}>{qtd}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{cat.label}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{cat.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Lista de receitas */}
          {receitas.length === 0 ? (
            <div className="empty-state">
              <Wheat size={48} />
              <h3>Nenhuma receita cadastrada</h3>
              <p>Clique em "Nova Receita" para começar.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {receitas.map(receita => {
                const cat = CATEGORIAS[receita.categoria] || {};
                const isExpanded = expandedId === receita.id;
                return (
                  <div key={receita.id} className="card" style={{ borderLeft: `4px solid ${cat.cor || '#ccc'}` }}>
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer' }}
                      onClick={() => toggleDetalhe(receita.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <strong>{receita.nome}</strong>
                        <span style={{
                          backgroundColor: `${cat.cor}22`, color: cat.cor,
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
                        }}>
                          {cat.label}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {receita.total_ingredientes || 0} ingredientes • Rende {receita.rendimento_kg || 0} kg
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button className="btn btn-primary" onClick={e => { e.stopPropagation(); abrirProduzir(receita); }}
                          style={{ fontSize: '12px', padding: '5px 12px' }}>
                          <Play size={13} /> Produzir
                        </button>
                        <button className="btn btn-secondary" onClick={e => { e.stopPropagation(); abrirModal(receita); }}
                          style={{ fontSize: '12px', padding: '5px 10px' }}>
                          <Edit size={13} />
                        </button>
                        <button className="btn btn-secondary" onClick={e => { e.stopPropagation(); excluirReceita(receita.id); }}
                          style={{ fontSize: '12px', padding: '5px 10px', color: '#dc2626' }}>
                          <Trash2 size={13} />
                        </button>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && receita.ingredientes && (
                      <div style={{ padding: '0 16px 14px', borderTop: '1px solid #e5e7eb' }}>
                        <table style={{ width: '100%', marginTop: '10px', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                              <th style={{ textAlign: 'left', padding: '6px' }}>Ingrediente</th>
                              <th style={{ textAlign: 'right', padding: '6px' }}>Qtd/Batelada (kg)</th>
                              <th style={{ textAlign: 'right', padding: '6px' }}>%</th>
                              <th style={{ textAlign: 'right', padding: '6px' }}>Estoque Atual</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receita.ingredientes.map((ing, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '6px' }}>{ing.produto_nome}</td>
                                <td style={{ padding: '6px', textAlign: 'right' }}>{ing.quantidade_kg}</td>
                                <td style={{ padding: '6px', textAlign: 'right' }}>{ing.percentual || '-'}%</td>
                                <td style={{ padding: '6px', textAlign: 'right', color: ing.estoque_disponivel < ing.quantidade_kg ? '#dc2626' : '#16a34a', fontWeight: 'bold' }}>
                                  {ing.estoque_disponivel ?? 0} kg
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ABA HISTÓRICO ── */}
      {abaAtiva === 'historico' && (
        <div>
          {producoes.length === 0 ? (
            <div className="empty-state">
              <BarChart3 size={48} />
              <h3>Nenhuma produção registrada</h3>
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Receita</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Categoria</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Bateladas</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Total (kg)</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Custo</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Lote</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Usuário</th>
                </tr>
              </thead>
              <tbody>
                {producoes.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px' }}>{formatDateBR(p.data_producao)}</td>
                    <td style={{ padding: '10px', fontWeight: '600' }}>{p.receita_nome}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        backgroundColor: `${CATEGORIAS[p.categoria]?.cor || '#666'}22`,
                        color: CATEGORIAS[p.categoria]?.cor || '#666',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {CATEGORIAS[p.categoria]?.label || p.categoria}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{p.multiplicador}x</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>{p.quantidade_total_kg} kg</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{p.custo_total ? `R$ ${parseFloat(p.custo_total).toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '10px' }}>{p.lote_producao || '—'}</td>
                    <td style={{ padding: '10px', color: '#6b7280' }}>{p.usuario_nome || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ABA CONSUMO DIÁRIO ── */}
      {abaAtiva === 'automatico' && (
        <div>
          {programacoes.length === 0 ? (
            <div className="empty-state">
              <CalendarClock size={48} />
              <h3>Nenhum consumo diário programado</h3>
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Receita</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Grupo</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Kg/dia</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Início</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Último lançamento</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {programacoes.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px', fontWeight: 600 }}>{p.receita_nome}</td>
                    <td style={{ padding: '10px' }}>{p.grupo_tipo === 'lote' ? `Lote: ${p.lote_nome || p.lote_id}` : GRUPOS_CONSUMO[p.grupo_tipo] || p.grupo_tipo}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{p.quantidade_kg} kg</td>
                    <td style={{ padding: '10px' }}>{formatDateBR(p.data_inicio)}</td>
                    <td style={{ padding: '10px' }}>{p.ultimo_lancamento ? formatDateBR(p.ultimo_lancamento) : '—'}</td>
                    <td style={{ padding: '10px' }}><span className={`badge ${p.ativo ? 'badge-green' : 'badge-gray'}`}>{p.ativo ? 'Ativo' : 'Inativo'}</span></td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      {p.ativo ? <button className="btn btn-secondary btn-sm" onClick={() => desativarProgramacao(p.id)}><Trash2 size={13} /></button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ABA CONSUMO ── */}
      {abaAtiva === 'consumo' && (
        <div>
          {consumos.length === 0 ? (
            <div className="empty-state">
              <Package size={48} />
              <h3>Nenhum consumo registrado</h3>
              <p>Clique em "Registrar Consumo" para lançar o uso de ração.</p>
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Receita</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Grupo</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Qtd (kg)</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Custo Total</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Animais</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Custo/Animal</th>
                </tr>
              </thead>
              <tbody>
                {consumos.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px' }}>{formatDateBR(c.data)}</td>
                    <td style={{ padding: '10px', fontWeight: '600' }}>{c.receita_nome}</td>
                    <td style={{ padding: '10px' }}>{c.grupo_tipo === 'lote' ? `Lote: ${c.lote_nome || c.lote_id}` : GRUPOS_CONSUMO[c.grupo_tipo] || c.grupo_tipo}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{c.quantidade_kg} kg</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{c.custo_total ? `R$ ${parseFloat(c.custo_total).toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{c.num_animais ?? '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{c.custo_por_animal ? `R$ ${parseFloat(c.custo_por_animal).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── MODAL CONSUMO ── */}
      {modalConsumo && (
        <div className="modal-overlay" onClick={() => setModalConsumo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Registrar Consumo de Ração</h2>
              <button onClick={() => setModalConsumo(false)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              <div className="form-group">
                <label>Receita *</label>
                <select className="input" value={formConsumo.receita_id}
                  onChange={e => setFormConsumo(p => ({ ...p, receita_id: e.target.value }))}>
                  <option value="">Selecione a receita...</option>
                  {receitas.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Data *</label>
                  <input type="date" className="input" value={formConsumo.data}
                    onChange={e => setFormConsumo(p => ({ ...p, data: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Quantidade (kg) *</label>
                  <input type="number" className="input" step="0.1" min="0.1" value={formConsumo.quantidade_kg}
                    onChange={e => setFormConsumo(p => ({ ...p, quantidade_kg: e.target.value }))}
                    placeholder="Ex: 25.0" />
                </div>
              </div>
              <div className="form-group">
                <label>Grupo de Animais *</label>
                <select className="input" value={formConsumo.grupo_tipo}
                  onChange={e => setFormConsumo(p => ({ ...p, grupo_tipo: e.target.value, lote_id: '' }))}>
                  {Object.entries(GRUPOS_CONSUMO).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              {formConsumo.grupo_tipo === 'lote' && (
                <div className="form-group">
                  <label>Lote *</label>
                  <select className="input" value={formConsumo.lote_id}
                    onChange={e => setFormConsumo(p => ({ ...p, lote_id: e.target.value }))}>
                    <option value="">Selecione o lote...</option>
                    {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Observações</label>
                <textarea className="input" rows={2} value={formConsumo.observacao}
                  onChange={e => setFormConsumo(p => ({ ...p, observacao: e.target.value }))}
                  placeholder="Opcional..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalConsumo(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={registrarConsumo}>
                <Package size={16} /> Registrar Consumo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL RECEITA ── */}
      {modalAberto && (
        <div className="modal-overlay" onClick={() => setModalAberto(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '85vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>{editandoId ? 'Editar Receita' : 'Nova Receita'}</h2>
              <button onClick={() => setModalAberto(false)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              <div className="form-group">
                <label>Nome da Receita</label>
                <input className="input" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Ração Cordeiro Amamentação" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Categoria</label>
                  <select className="input" value={form.categoria} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))}>
                    {categoriasComReceita.map(k => (
                      <option key={k} value={k}>{CATEGORIAS[k].label} — {CATEGORIAS[k].desc}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rendimento por batelada (kg)</label>
                  <input type="number" className="input" value={form.rendimento_kg} onChange={e => setForm(p => ({ ...p, rendimento_kg: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea className="input" rows={2} value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
              </div>

              {/* Ingredientes */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontWeight: 'bold' }}>Ingredientes (Matérias-Primas)</label>
                  <button className="btn btn-secondary" onClick={adicionarIngrediente} style={{ fontSize: '12px', padding: '4px 10px' }}>
                    <Plus size={13} /> Adicionar
                  </button>
                </div>
                {form.ingredientes.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>
                    Nenhum ingrediente. Clique em "Adicionar" acima.
                    {materiasPrimas.length === 0 && (
                      <span style={{ display: 'block', color: '#dc2626', marginTop: '4px' }}>
                        Atenção: nenhuma matéria-prima cadastrada no estoque. Cadastre produtos do tipo "Matéria-Prima" primeiro.
                      </span>
                    )}
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {form.ingredientes.map((ing, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select className="input" style={{ flex: 2 }} value={ing.produto_id}
                          onChange={e => atualizarIngrediente(idx, 'produto_id', Number(e.target.value))}>
                          <option value="">Selecione...</option>
                          {materiasPrimas.map(mp => (
                            <option key={mp.id} value={mp.id}>{mp.nome} ({mp.saldo_atual} kg disp.)</option>
                          ))}
                        </select>
                        <input type="number" className="input" style={{ flex: 1 }} placeholder="kg"
                          value={ing.quantidade_kg} onChange={e => atualizarIngrediente(idx, 'quantidade_kg', Number(e.target.value))} />
                        <input type="number" className="input" style={{ width: '70px' }} placeholder="%"
                          value={ing.percentual} onChange={e => atualizarIngrediente(idx, 'percentual', Number(e.target.value))} />
                        <button onClick={() => removerIngrediente(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalAberto(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarReceita}>
                {editandoId ? 'Atualizar' : 'Criar Receita'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PROGRAMAÇÃO ── */}
      {modalProgramacao && (
        <div className="modal-overlay" onClick={() => setModalProgramacao(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>Programar Consumo Diário</h2>
              <button onClick={() => setModalProgramacao(false)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              <div className="form-group">
                <label>Receita *</label>
                <select className="input" value={formProgramacao.receita_id}
                  onChange={e => setFormProgramacao(p => ({ ...p, receita_id: e.target.value }))}>
                  <option value="">Selecione a receita...</option>
                  {receitas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Kg por dia *</label>
                  <input type="number" className="input" step="0.1" min="0.1" value={formProgramacao.quantidade_kg}
                    onChange={e => setFormProgramacao(p => ({ ...p, quantidade_kg: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Data inicial *</label>
                  <input type="date" className="input" value={formProgramacao.data_inicio}
                    onChange={e => setFormProgramacao(p => ({ ...p, data_inicio: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Grupo *</label>
                <select className="input" value={formProgramacao.grupo_tipo}
                  onChange={e => setFormProgramacao(p => ({ ...p, grupo_tipo: e.target.value, lote_id: '' }))}>
                  {Object.entries(GRUPOS_CONSUMO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {formProgramacao.grupo_tipo === 'lote' && (
                <div className="form-group">
                  <label>Lote *</label>
                  <select className="input" value={formProgramacao.lote_id}
                    onChange={e => setFormProgramacao(p => ({ ...p, lote_id: e.target.value }))}>
                    <option value="">Selecione o lote...</option>
                    {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Observações</label>
                <textarea className="input" rows={2} value={formProgramacao.observacao}
                  onChange={e => setFormProgramacao(p => ({ ...p, observacao: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalProgramacao(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarProgramacao}>
                <CalendarClock size={16} /> Programar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PRODUZIR ── */}
      {modalProduzir && (
        <div className="modal-overlay" onClick={() => setModalProduzir(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Produzir Ração</h2>
              <button onClick={() => setModalProduzir(null)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <strong>{modalProduzir.nome}</strong>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  {CATEGORIAS[modalProduzir.categoria]?.label} • Rende {modalProduzir.rendimento_kg} kg/batelada
                </div>
              </div>

              {capacidade && (
                <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e40af' }}>
                    Capacidade máxima: {capacidade.bateladas_possiveis} batelada(s) = {capacidade.producao_maxima_kg} kg
                  </div>
                  {capacidade.limitante && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> Limitado por: {capacidade.limitante}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Quantidade de bateladas</label>
                <input type="number" className="input" value={multiplicador} min={0.1} step={0.1}
                  max={capacidade?.bateladas_possiveis || 999}
                  onChange={e => setMultiplicador(Number(e.target.value))} />
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  Total produzido: <strong>{((modalProduzir.rendimento_kg || 0) * multiplicador).toFixed(1)} kg</strong>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalProduzir(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={executarProducao}
                disabled={multiplicador <= 0 || (capacidade && multiplicador > capacidade.bateladas_possiveis)}>
                <Play size={16} /> Produzir {((modalProduzir.rendimento_kg || 0) * multiplicador).toFixed(1)} kg
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Racoes;
